import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertTriangle, CheckCircle2, HelpCircle, Lightbulb, Printer, RotateCcw, Star, Target, XCircle } from 'lucide-react';
import { getChapter, MPD_META, MPD_DOSSIERS, MPD_REFS } from '../../data/premierDepartData';
import { readProgress, writeProgress } from './mpdProgress';
import { AngleMortExplorer } from './AngleMortExplorer';

const CALLOUTS = {
  retenir: { icon: Star, cls: 'border-orange-500 bg-orange-50 text-orange-900', label: 'À retenir' },
  attention: { icon: AlertTriangle, cls: 'border-red-500 bg-red-50 text-red-900', label: 'Attention' },
  savais: { icon: Lightbulb, cls: 'border-sky-500 bg-sky-50 text-sky-900', label: 'Le savais-tu ?' },
  question: { icon: HelpCircle, cls: 'border-violet-500 bg-violet-50 text-violet-900', label: 'Question à se poser' },
};

function Scenario({ b }) {
  const [picked, setPicked] = useState({});
  return (
    <div className="rounded-2xl border-2 border-[#e8e0d0] bg-white overflow-hidden my-5" data-testid={`mpd-scenario-${b.num}`}>
      {b.src && <img src={b.src} alt={b.title} loading="lazy" className="w-full aspect-[2/1] object-cover" />}
      <div className="p-4 sm:p-5">
        <p className="text-[10.5px] font-black uppercase tracking-[0.2em] text-orange-600 mb-1">Situation {b.num}</p>
        <h4 className="font-black text-[16px] mb-2">{b.title}</h4>
        <p className="text-[13.5px] text-[#3d362c] mb-4 italic">{b.situation}</p>
        {b.questions.map((q, qi) => {
          const sel = picked[qi];
          const answered = sel !== undefined;
          return (
            <div key={qi} className="mb-4">
              <p className="font-bold text-[13.5px] mb-2">Question {qi + 1} — {q.q}</p>
              <div className="space-y-1.5">
                {q.options.map((opt, oi) => {
                  const good = answered && oi === q.answer;
                  const bad = answered && sel === oi && oi !== q.answer;
                  return (
                    <button key={oi} type="button" disabled={answered} onClick={() => setPicked({ ...picked, [qi]: oi })}
                      data-testid={`mpd-scn${b.num}-q${qi}-o${oi}`}
                      className={`w-full flex items-start gap-2 px-3 py-2 rounded-lg border text-left text-[13px] ${
                        good ? 'border-emerald-400 bg-emerald-50' : bad ? 'border-red-400 bg-red-50' : 'border-[#eee5d4] hover:border-orange-300'}`}>
                      <span className="font-black text-[11.5px] mt-0.5">{String.fromCharCode(65 + oi)}.</span>
                      <span className="flex-1">{opt}</span>
                      {good && <CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />}
                      {bad && <XCircle size={15} className="text-red-500 shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
              {answered && <p className="mt-1.5 text-[12.5px] text-[#5a5245] pl-1">💡 {q.explain}</p>}
            </div>
          );
        })}
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {b.erreur && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12px]"><span className="font-black text-red-700">Erreur fréquente :</span> {b.erreur}</div>}
          {b.competence && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px]"><span className="font-black text-emerald-700">Compétence travaillée :</span> {b.competence}</div>}
        </div>
      </div>
    </div>
  );
}

function Block({ b }) {
  if (b.t === 'angleMort') return <AngleMortExplorer />;
  if (b.t === 'img') return (
    <figure className="my-5 rounded-2xl overflow-hidden border border-[#e8e0d0] bg-white">
      <img src={b.src} alt={b.cap || ''} loading="lazy" className="w-full aspect-[2/1] object-cover" />
      {b.cap && <figcaption className="px-3 py-2 text-[11.5px] font-semibold text-[#5a5245]">{b.cap}</figcaption>}
    </figure>
  );
  if (b.t === 'scenario') return <Scenario b={b} />;
  if (b.t === 'p') return <p className="text-[14.5px] leading-relaxed text-[#3d362c] mb-3">{b.text}</p>;
  if (b.t === 'sub') return <h4 className="font-black text-[15px] mt-4 mb-1.5">{b.text}</h4>;
  if (b.t === 'list') return <ul className="mb-3 space-y-1.5">{b.items.map((it, i) => <li key={i} className="flex gap-2 text-[14px] text-[#3d362c]"><span className="text-orange-500 font-black mt-0.5">•</span>{it}</li>)}</ul>;
  if (b.t === 'olist') return <ol className="mb-3 space-y-1.5">{b.items.map((it, i) => <li key={i} className="flex gap-2.5 text-[14px] text-[#3d362c]"><span className="shrink-0 w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-black flex items-center justify-center mt-0.5">{i + 1}</span>{it}</li>)}</ol>;
  if (b.t === 'formula') return <div className="my-4 rounded-xl bg-[#241f18] text-white text-center font-black text-[15px] px-4 py-3.5">{b.text}</div>;
  if (b.t === 'imggrid') return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 my-4">
      {b.items.map((it, i) => (
        <figure key={i} className="rounded-xl overflow-hidden border border-[#e8e0d0] bg-white">
          <img src={it.src} alt={it.label} loading="lazy" className="w-full aspect-[4/3] object-cover" />
          <figcaption className="px-2.5 py-2 text-[11.5px] font-semibold text-[#5a5245] leading-snug">{it.label}</figcaption>
        </figure>
      ))}
    </div>
  );
  if (b.t === 'phases') {
    const steps = b.steps || (b.items || []).map((it) => ({ title: it.step, desc: it.desc }));
    return (
      <div className="my-5 space-y-3">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-4 items-center rounded-2xl border border-[#e8e0d0] bg-white p-3">
            {s.src && <img src={s.src} alt={s.title} loading="lazy" className="w-28 sm:w-36 aspect-[4/3] object-cover rounded-xl shrink-0" />}
            <div>
              <p className="font-black text-[14.5px] mb-0.5"><span className="text-orange-600">{i + 1}.</span> {s.title}</p>
              <p className="text-[13px] text-[#5a5245] leading-snug">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (b.t === 'error') return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 mb-3">
      <p className="font-black text-[14px] mb-1.5 text-red-700">{b.title}</p>
      <p className="text-[13px] text-[#3d362c] mb-1"><span className="font-bold">Le risque :</span> {b.risque}</p>
      <p className="text-[13px] text-emerald-800"><span className="font-bold">Le bon réflexe :</span> {b.reflexe}</p>
    </div>
  );
  if (b.t === 'callout') {
    const c = CALLOUTS[b.kind] || CALLOUTS.retenir;
    const Icon = c.icon;
    return (
      <div className={`rounded-xl border-l-4 p-4 my-4 ${c.cls}`}>
        <p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-1"><Icon size={13} /> {b.title || c.label}</p>
        <p className="text-[13.5px] leading-relaxed">{b.text}</p>
      </div>
    );
  }
  return null;
}

function Checklist({ dossierId, slug, checklist }) {
  const key = `${dossierId}/${slug}`;
  const [checks, setChecks] = useState(() => readProgress().checks[key] || []);
  const toggle = (i) => {
    const next = checks.includes(i) ? checks.filter((x) => x !== i) : [...checks, i];
    setChecks(next);
    const p = readProgress(); p.checks[key] = next; writeProgress(p);
  };
  const reset = () => { setChecks([]); const p = readProgress(); p.checks[key] = []; writeProgress(p); };
  return (
    <div className="rounded-2xl border-2 border-orange-300 bg-white p-5 my-6 no-print-break" data-testid="mpd-checklist">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-black text-[16px]">☑ {checklist.title}</h3>
        <span className="text-[13px] font-black text-orange-600" data-testid="mpd-checklist-count">{checks.length}/{checklist.items.length}</span>
      </div>
      <p className="text-[12.5px] text-[#8a8070] mb-4">{checklist.goal}</p>
      <div className="space-y-2">
        {checklist.items.map((it, i) => (
          <button key={i} type="button" onClick={() => toggle(i)} data-testid={`mpd-check-${i}`}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl border text-left transition-colors ${checks.includes(i) ? 'border-emerald-300 bg-emerald-50' : 'border-[#eee5d4] hover:border-orange-300'}`}>
            {checks.includes(i) ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" /> : <span className="w-[18px] h-[18px] rounded-full border-2 border-[#d5c9b0] shrink-0" />}
            <span className={`text-[13.5px] font-semibold ${checks.includes(i) ? 'text-emerald-900' : 'text-[#3d362c]'}`}>{it}</span>
          </button>
        ))}
      </div>
      {checks.length === checklist.items.length && <p className="mt-3 text-[13px] font-bold text-emerald-700">✔ Checklist complète — je suis prêt pour le départ !</p>}
      <button type="button" onClick={reset} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#8a8070] hover:text-orange-600 no-print" data-testid="mpd-checklist-reset">
        <RotateCcw size={12} /> Réinitialiser la checklist
      </button>
    </div>
  );
}

function Quiz({ dossierId, slug, quiz }) {
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const allAnswered = Object.keys(answers).length === quiz.questions.length;
  const score = quiz.questions.reduce((s, q, i) => s + (answers[i] === q.answer ? 1 : 0), 0);
  const reveal = () => {
    setRevealed(true);
    const p = readProgress(); p.quiz[`${dossierId}/${slug}`] = { score, total: quiz.questions.length }; writeProgress(p);
  };
  return (
    <div className="rounded-2xl border-2 border-[#241f18]/15 bg-white p-5 my-6" data-testid="mpd-quiz">
      <h3 className="font-black text-[17px] mb-4">🎯 {quiz.title}</h3>
      <div className="space-y-5">
        {quiz.questions.map((q, qi) => (
          <div key={qi}>
            <p className="font-bold text-[14px] mb-2">Question {qi + 1} — {q.q}</p>
            <div className="space-y-1.5">
              {q.options.map((opt, oi) => {
                const sel = answers[qi] === oi;
                const good = revealed && oi === q.answer;
                const bad = revealed && sel && oi !== q.answer;
                return (
                  <button key={oi} type="button" disabled={revealed} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                    data-testid={`mpd-quiz-q${qi}-o${oi}`}
                    className={`w-full flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl border text-left text-[13.5px] transition-colors ${
                      good ? 'border-emerald-400 bg-emerald-50' : bad ? 'border-red-400 bg-red-50'
                      : sel ? 'border-orange-400 bg-orange-50' : 'border-[#eee5d4] hover:border-orange-300'}`}>
                    <span className="font-black text-[12px] mt-0.5">{String.fromCharCode(65 + oi)}.</span>
                    <span className="flex-1">{opt}</span>
                    {good && <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />}
                    {bad && <XCircle size={16} className="text-red-500 shrink-0 mt-0.5" />}
                  </button>
                );
              })}
            </div>
            {revealed && <p className="mt-1.5 text-[12.5px] text-[#5a5245] italic pl-1">💡 {q.explain}</p>}
          </div>
        ))}
      </div>
      {!revealed ? (
        <div className="mt-5">
          <button type="button" onClick={reveal} disabled={!allAnswered} data-testid="mpd-quiz-reveal"
            className={`px-5 h-11 rounded-full font-bold text-sm ${allAnswered ? 'bg-orange-600 hover:bg-orange-500 text-white' : 'bg-[#eee5d4] text-[#b5a98f] cursor-not-allowed'}`}>
            Voir mes réponses
          </button>
          {!allAnswered && <p className="mt-2 text-[12px] text-[#8a8070]">Répondez aux {quiz.questions.length} questions pour afficher le corrigé.</p>}
        </div>
      ) : (
        <div className="mt-5 rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 font-black text-orange-800 text-[15px]" data-testid="mpd-quiz-score">
          Mon score : {score}/{quiz.questions.length} {score === quiz.questions.length ? '— parfait ! 🏆' : score >= quiz.questions.length / 2 ? '— bien joué, relis les corrections.' : '— relis le chapitre puis réessaie.'}
        </div>
      )}
    </div>
  );
}

export default function PremierDepartChapitre() {
  const { dossierId, slug } = useParams();
  const data = useMemo(() => getChapter(dossierId, slug), [dossierId, slug]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    setDone(!!readProgress().done[`${dossierId}/${slug}`]);
  }, [dossierId, slug]);

  if (!data) return (
    <div className="min-h-screen bg-[#faf7f0] flex items-center justify-center">
      <Link to="/mon-premier-depart" className="text-orange-600 font-bold">Chapitre introuvable — retour au livret</Link>
    </div>
  );
  const { dossier, chapter, prev, next } = data;

  const markDone = () => {
    const p = readProgress(); p.done[`${dossierId}/${slug}`] = true; writeProgress(p); setDone(true);
  };

  return (
    <div className="min-h-screen bg-[#faf7f0] text-[#241f18]" data-testid="mpd-chapter">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Fil d'ariane */}
        <div className="flex items-center justify-between gap-2 mb-5 no-print">
          <Link to="/mon-premier-depart" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#8a8070] hover:text-orange-600" data-testid="mpd-back-home">
            <ArrowLeft size={14} /> Mon premier départ
          </Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#8a8070] hover:text-orange-600" data-testid="mpd-chapter-print">
            <Printer size={13} /> Imprimer / PDF
          </button>
        </div>

        <p className="text-[10.5px] font-black uppercase tracking-[0.22em] text-orange-600 mb-1">{dossier.num} · {chapter.num}</p>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>{chapter.title}</h1>
        {chapter.objectif && (
          <p className="flex items-start gap-2 text-[13.5px] font-semibold text-[#5a5245] mb-4"><Target size={16} className="text-orange-500 shrink-0 mt-0.5" /> Objectif : {chapter.objectif}</p>
        )}
        {chapter.image && <img src={chapter.image} alt={chapter.title} className="rounded-2xl border border-white shadow-md w-full object-cover aspect-[2/1] mb-7" />}

        <div key={`${dossierId}/${slug}`}>
        {chapter.sections.map((s, si) => (
          <section key={si} className="mb-7">
            <h2 className="text-[17px] font-black mb-3 pb-1.5 border-b border-[#e8e0d0]">{s.title}</h2>
            {s.blocks.map((b, bi) => <Block key={bi} b={b} />)}
          </section>
        ))}

        {chapter.checklist && <Checklist dossierId={dossierId} slug={slug} checklist={chapter.checklist} />}
        {chapter.quiz && <Quiz dossierId={dossierId} slug={slug} quiz={chapter.quiz} />}

        {chapter.bilan && (
          <div className="rounded-2xl bg-[#241f18] text-white p-5 my-6" data-testid="mpd-bilan">
            <h3 className="font-black text-[16px] mb-1">📋 {chapter.bilan.title}</h3>
            <p className="text-[13px] text-white/70 mb-3">{chapter.bilan.intro}</p>
            <ul className="space-y-1.5 mb-4">
              {chapter.bilan.items.map((it, i) => <li key={i} className="flex gap-2 text-[13.5px]"><CheckCircle2 size={15} className="text-orange-400 shrink-0 mt-0.5" /> {it}</li>)}
            </ul>
            <p className="text-[13px] font-bold text-orange-300">Mon prochain objectif : {chapter.bilan.next}</p>
          </div>
        )}
        </div>

        {/* Renvois croisés */}
        {!next && (MPD_REFS[dossier.id] || []).length > 0 && (
          <div className="rounded-2xl border border-[#e8e0d0] bg-white p-5 mt-6 no-print" data-testid="mpd-refs">
            <h3 className="text-[13px] font-black uppercase tracking-wide text-orange-700 mb-3">À consulter aussi dans le livret</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {MPD_REFS[dossier.id].map((rid) => {
                const rd = MPD_DOSSIERS.find((d) => d.id === rid);
                if (!rd) return null;
                return (
                  <Link key={rid} to={`/mon-premier-depart/${rd.id}/${rd.chapters[0].slug}`} data-testid={`mpd-ref-${rid}`}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-[#eee5d4] hover:border-orange-300 hover:bg-orange-50/50 transition-colors">
                    <ArrowRight size={14} className="text-orange-500 shrink-0" />
                    <span className="text-[13px] font-semibold leading-tight">{rd.num} — {rd.title}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-8 pt-5 border-t border-[#e8e0d0] no-print">
          <button type="button" onClick={markDone} disabled={done} data-testid="mpd-mark-done"
            className={`inline-flex items-center gap-2 px-5 h-11 rounded-full font-bold text-sm ${done ? 'bg-emerald-100 text-emerald-700 cursor-default' : 'bg-orange-600 hover:bg-orange-500 text-white'}`}>
            <CheckCircle2 size={16} /> {done ? 'Chapitre terminé ✔' : "J'ai terminé ce chapitre"}
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 mt-5 no-print">
          {prev ? (
            <Link to={`/mon-premier-depart/${dossier.id}/${prev.slug}`} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#5a5245] hover:text-orange-600" data-testid="mpd-prev-chapter">
              <ArrowLeft size={14} /> {prev.num}
            </Link>
          ) : <span />}
          {next ? (
            <Link to={`/mon-premier-depart/${dossier.id}/${next.slug}`} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-orange-600 hover:text-orange-500" data-testid="mpd-next-chapter">
              {next.num} — {next.title} <ArrowRight size={14} />
            </Link>
          ) : (
            <Link to="/mon-premier-depart" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-orange-600" data-testid="mpd-back-end">
              Retour au sommaire du livret <ArrowRight size={14} />
            </Link>
          )}
        </div>
        <p className="mt-8 text-[11px] text-[#b5a98f]">{MPD_META.cobrand} · Contenus mis à jour le {MPD_META.updatedAt} · Support complémentaire — ne remplace pas la formation pratique encadrée.</p>
      </div>
    </div>
  );
}
